import React, { useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { SearchOutlined, DeleteOutlined, FormOutlined } from '@ant-design/icons';
import { FaInfoCircle } from 'react-icons/fa';
import { BsArrowUpRightSquareFill } from 'react-icons/bs';
import { Button, Input, Space, Table, Tag, Popconfirm, DatePicker, message, Tooltip } from 'antd';
import Highlighter from 'react-highlight-words';
import {
  beautifyKey,
  convertKeyToCamelCase,
  formatTableCellValue,
  formatUkDateTime,
  getDtoFieldProp,
  isDateTimeDtoField,
} from '../../utils/functions';
import { getTranslatedFilters } from './utils/fieldFilters';
import dayjs from '../../utils/dayjs';
import { useTranslation } from 'react-i18next';
import TableTitle from './common/TableTitle';
import RecordDetailsModal from './utils/RecordDetailsModal.component';
import debounce from 'lodash/debounce';

const { RangePicker } = DatePicker;

const ListItemsDataTable = ({
  dtoConfig,
  data,
  filter,
  onFilterChange,
  currentPage,
  totalPages,
  totalCount,
  limit = 10,
  loading,
  handleDeleteRow,
  handleSaveRow,
  onStartEditRow,
  onDataUpdated,
  handleEditForm,
  handleViewForm,
  getEntityDetails,
  detailsRecord,
  detailsLoading,
  detailsError,
  error,
  onRetry,
  locale: tableLocale,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [editingRowId, setEditingRowId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [searchProp, setSearchProp] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const tableRef = useRef(null);
  const searchInput = useRef(null);

  const { statusFilters, alertFilters, valueFilters, emailConfirmedFilters, isOptedOutFilters, isNhsNumberValidFilters, dataQualityStatusFilters, omopImportStatusFilters, genderFilters, roleFilters, recordTypeFilters, recordTypeTagLabel } = getTranslatedFilters();

  useEffect(() => {
    setSearchText(filter.query || '');
    setSearchedColumn(filter.filter || '');
    setSearchProp(filter.filter || '');
    setDateRange(filter.startDate && filter.endDate ? [dayjs(filter.startDate), dayjs(filter.endDate)] : []);
  }, [filter]);

  // Initialize columns
  useEffect(() => {
    if (!dtoConfig) return;
    
    const renderedColumns = renderColumns()?.map((column) => {
      if (column.filtered) {
        const { render: searchRender, ...searchProps } = getColumnSearchProps(column.dataIndex);
        return {
          ...column,
          ...searchProps,
          render: column.render ?? searchRender,
        };
      }
      return column;
    });
    setColumns(renderedColumns);
  }, [dtoConfig, selectedRowKeys]);

  // Proper ResizeObserver cleanup
  useEffect(() => {
    let resizeObserver;

    if (tableRef.current) {
      resizeObserver = new ResizeObserver(
        debounce(() => {
          // Optional: Update any table width state if needed
        }, 100)
      );
      
      resizeObserver.observe(tableRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
    onFilterChange((prevFilter) => ({
      ...prevFilter,
      query: selectedKeys[0],
      filter: dataIndex,
    }));
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const handleBulkDelete = () => {
    setIsBulkDeleting(true);
    selectedRowKeys.forEach(key => {
      handleDeleteRow(key);
    });
    setSelectedRowKeys([]);
    setIsBulkDeleting(false);
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setViewModalVisible(true);
  };

  useEffect(() => {
    if (viewModalVisible && selectedRecord?.id && getEntityDetails) {
      dispatch(getEntityDetails(selectedRecord.id));
    }
  }, [viewModalVisible, selectedRecord?.id, getEntityDetails, dispatch]);

  const handleCloseModal = () => {
    setViewModalVisible(false);
    setSelectedRecord(null);
  };

  const modalDetailsRecord = getEntityDetails
    ? (detailsRecord?.id === selectedRecord?.id ? detailsRecord : null)
    : selectedRecord;

  const handleSearchPropChange = (value) => {
    setSearchProp(value || '');
    onFilterChange({ ...filter, filter: value || '' });
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      const startDate = dates[0].toISOString();
      const endDate = dates[1].toISOString();
      setDateRange(dates);
      onFilterChange({ ...filter, startDate, endDate });
    } else {
      setDateRange([]);
      onFilterChange({ ...filter, startDate: null, endDate: null });
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: '8px' }}>
        <Input
          ref={searchInput}
          placeholder={`${t("Search")} ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {t("Search")}
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            {t("Reset")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible) => {
      if (visible && searchInput.current) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const renderColumns = () => {
    const actionColumn = {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'left',
      width: 180,
      render: (_, record) => {
        const buttons = [];

        buttons.push(
          <Tooltip key="info" title={t('View Details')}>
            <Button
              type="text"
              size="small"
              icon={<FaInfoCircle />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
        );

        if (handleViewForm) {
          buttons.push(
            <Button
              key="view"
              type="text"
              size="small"
              onClick={() => handleViewForm(record.id)}
            >
              {t('View')}
            </Button>
          );
        }

        if (handleEditForm) {
          buttons.push(
            <Button
              key="edit"
              type="text"
              size="small"
              icon={<BsArrowUpRightSquareFill />}
              onClick={() => handleEditForm(record.id)}
            >
              {t('View')}
            </Button>
          );
        }

        if (!handleViewForm && !handleEditForm) {
          buttons.push(
            <Button
              key="view-default"
              type="text"
              size="small"
              icon={<FormOutlined />}
              onClick={() => handleEditForm?.(record.id)}
            >
              {t('View')}
            </Button>
          );
        }

        if (handleDeleteRow) {
          buttons.push(
            <Popconfirm
              key="delete"
              title={t('Delete item')}
              description={t('Are you sure you want to delete this item?')}
              okText={t('Yes')}
              cancelText={t('No')}
              onConfirm={() => handleDeleteRow(record.id)}
            >
              <Tooltip title={t('Delete')}>
                <Button type="text" size="small" icon={<DeleteOutlined />} danger />
              </Tooltip>
            </Popconfirm>
          );
        }

        return <Space size={4}>{buttons}</Space>;
      },
    };

    let columnsConfig = [actionColumn];
    
    Object.keys(dtoConfig || {}).forEach((key) => {
      const fieldProps = dtoConfig[key];
      if (!getDtoFieldProp(fieldProps, 'isSummaryProp', 'IsSummaryProp')) return;

      let filters = [];
      let render = (text) => <span>{formatTableCellValue(text)}</span>;
      let onFilter = null;
      let sorter = null;

      const fieldKey = convertKeyToCamelCase(key);
      const isDateTimeField = isDateTimeDtoField(fieldProps);

        switch (key) {
          case 'Result':
          case 'Order':
            filters = [{ text: 'A', value: 'A' }];
            render = (text, record) => (
              editingRow === record.id ? (
                <Input
                  size="small"
                  value={text}
                  onChange={(e) => onDataUpdated({
                    ...record,
                    [fieldKey]: e.target.value,
                  })}
                />
              ) : (
                <strong>{text}</strong>
              )
            );
            onFilter = (value, record) => record[fieldKey]?.toLowerCase().includes(value.toLowerCase());
            // Check if the field contains numeric values
            sorter = (a, b) => {
              const aVal = a[fieldKey];
              const bVal = b[fieldKey];
              if (typeof aVal === 'number' && typeof bVal === 'number') {
                return aVal - bVal;
              }
              return (aVal?.toString() || '').localeCompare(bVal?.toString() || '');
            };
            break;

          case 'Alert':
            filters = alertFilters;   
            render = (text) => (
              <Tag color={text?.toString().toLowerCase()} style={{ margin: 0 }}>
                {text}
              </Tag>
            );
            break;

          case 'Status':
          case 'QCScheduleStatus':
          case 'ValidationStatus':
          case 'QCResultStatus':
          case 'EnrollmentStatus':
          case 'ApprovalStatus':
          case 'ProcessingStatus':
            filters = statusFilters;
            render = (text) => (
              <Tag
                style={{ margin: 0 }}
                color={
                  text === 'INACTIVE' ? 'red' :
                  text === 'PENDING' ? 'yellow' :
                  text === 'COMPLETED' || text === 'APPROVED' || text === 'COMPLETED' || text === 'ACTIVE' ? 'green' :
                  text === 'SUSPENDED' ? 'orange' :
                  text === 'REQUESTED' ? 'yellow' :
                  text === 'CANCELLED' ? 'red' :
                  text === 'REJECTED' ? 'red' :
                  text === 'PENDING_REVIEW' ? 'yellow' :
                  undefined
                }
              >
                {text?.toString().toUpperCase() || "-"}
              </Tag>
            );
            onFilter = (value, record) => record[fieldKey] === value;
            break;

          case 'Gender':
            filters = genderFilters;
            render = (text) => <strong>{text}</strong>;
            onFilter = (value, record) => record[fieldKey] === value;
            break;

          case 'EmailConfirmed':
          case 'IsActive':
          case 'IsAvailableForEmergency':
          case 'IsOccupied':
          case 'IsAvailable':
          case 'IsIsolation':
          case 'HasOxygenSupply':
          case 'HasVentilator':
          case 'IsOptedIn':
            filters = emailConfirmedFilters;
            render = (text) => (
              <Tag style={{ margin: 0 }} color={text ? 'green' : 'red'}>
                {text ? t('Yes') : t('No')}
              </Tag>
            );
            onFilter = (value, record) => record[fieldKey] === value;
            break;

          case 'IsOptedOut':
          case 'isOptedOut':
            filters = isOptedOutFilters;
            render = (text) => (
              <Tag style={{ margin: 0 }} color={text ? 'red' : 'green'}>
                {text ? t('Yes') : t('No')}
              </Tag>
            );
            onFilter = (value, record) => record[fieldKey] === value;
            break;

          case 'IsNhsNumberValid':
          case 'isNhsNumberValid':
            filters = isNhsNumberValidFilters;
            render = (text) => (
              <Tag style={{ margin: 0 }} color={text === true ? 'green' : 'default'}>
                {text === true ? t('Yes') : text === false ? t('No') : '-'}
              </Tag>
            );
            onFilter = (value, record) => record[fieldKey] === value;
            break;

          case 'DataQualityStatus':
          case 'dataQualityStatus':
            filters = dataQualityStatusFilters;
            render = (text) => {
              const ragMap = { R: '#ff4d4f', Red: '#ff4d4f', G: '#52c41a', Green: '#52c41a', A: '#faad14', Amber: '#faad14' };
              const normalized = (text || '').toString().trim();
              const color = ragMap[normalized];
              if (!color) return <span>{text ?? '-'}</span>;
              return (
                <Tooltip title={text ?? '-'}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: color,
                    }}
                  />
                </Tooltip>
              );
            };
            onFilter = (value, record) => record[fieldKey] === value;
            break;

          case 'OmopImportStatus':
          case 'omopImportStatus':
            filters = omopImportStatusFilters;
            render = (text) => (
              <Tag
                style={{ margin: 0 }}
                color={
                  text === 'Imported' ? 'green' :
                  text === 'Pending' ? 'default' :
                  text === 'Skipped' ? 'orange' :
                  text === 'Failed' ? 'red' :
                  undefined
                }
              >
                {text ?? '-'}
              </Tag>
            );
            onFilter = (value, record) => record[fieldKey] === value;
            break;

          case 'Roles':
            filters = roleFilters;
            render = (text) => (
              <Space size={2} wrap style={{ margin: 0 }}>
                {Array.isArray(text) && text.map((role) => (
                  <Tag key={role} color="blue" style={{ margin: 0 }}>{role}</Tag>
                ))}
              </Space>
            );
            onFilter = (value, record) => record[fieldKey]?.includes(value);
            break;

          case 'RecordType':
            filters = recordTypeFilters;
            render = (text) => (
              <Tag color="blue" style={{ margin: 0 }}>
                {recordTypeTagLabel(text)}
              </Tag>
            );
            onFilter = (value, record) => record[fieldKey] === value;
            break;

          default:
            // Add default sorter that handles both numeric and string values
            sorter = (a, b) => {
              const aVal = a[fieldKey];
              const bVal = b[fieldKey];
              if (typeof aVal === 'number' && typeof bVal === 'number') {
                return aVal - bVal;
              }
              return (aVal?.toString() || '').localeCompare(bVal?.toString() || '');
            };
        }

        if (isDateTimeField) {
          render = (text) => <span>{formatUkDateTime(text)}</span>;
          sorter = (a, b) => {
            const aDate = dayjs(a[fieldKey]);
            const bDate = dayjs(b[fieldKey]);
            if (!aDate.isValid() && !bDate.isValid()) return 0;
            if (!aDate.isValid()) return 1;
            if (!bDate.isValid()) return -1;
            return aDate.valueOf() - bDate.valueOf();
          };
        }

        const translatedLabel = t(key);
        const columnLabel = translatedLabel !== key ? translatedLabel : beautifyKey(key);

        columnsConfig.push({
          title: (
            <Tooltip title={key}>
              <span style={{ fontSize: '12px' }}>{columnLabel}</span>
            </Tooltip>
          ),
          dataIndex: fieldKey,
          key: fieldKey,
          filtered: !isDateTimeField && filters.length === 0,
          filters: filters,
          render: render,
          onFilter: onFilter,
          sorter: sorter,
          ellipsis: true,
          width: key === 'Actions' ? 150 : undefined,
        });
    });

    return columnsConfig;
  };

  return (
    <div ref={tableRef} style={{ width: '100%', overflow: 'auto' }}>
      {error && (
        <div style={{ margin: '16px 0', textAlign: 'center', color: 'red' }}>
          <p>{typeof error === 'string' ? error : 'Failed to load data.'}</p>
          {onRetry && (
            <Button type="primary" onClick={onRetry} size="small">Retry</Button>
          )}
        </div>
      )}
      <Table
        size="small"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
        }}
        columns={columns}
        dataSource={error ? [{ id: 'error', error: true }] : data}
        loading={loading}
        scroll={{ x: 1500 }}
        sticky
        style={{ marginTop: 20 }}
        tableLayout="auto"
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: totalCount,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
          pageSizeOptions: [10, 20, 50, 100],
          size: "small",
          onChange: (page, pageSize) => {
            onFilterChange({ ...filter, page, limit: pageSize });
          },
        }}
        rowKey="id"
        title={dtoConfig ? () => (
          <TableTitle
            dtoConfig={dtoConfig}
            searchProp={searchProp}
            handleSearchPropChange={handleSearchPropChange}
            searchText={searchText}
            setSearchText={setSearchText}
            onFilterChange={onFilterChange}
            filter={filter}
            dateRange={dateRange}
            handleDateRangeChange={handleDateRangeChange}
            limit={limit}
            setSearchedColumn={setSearchedColumn}
            setSearchProp={setSearchProp}
            setDateRange={setDateRange}
            selectedRowKeys={selectedRowKeys}
            handleBulkDelete={handleBulkDelete}
            isBulkDeleting={isBulkDeleting}
            t={t}
          />
        ) : null}
        locale={
          error
            ? { emptyText: <span style={{ color: 'red' }}>Failed to load data</span> }
            : tableLocale
        }
      />
      <RecordDetailsModal
        visible={viewModalVisible}
        onClose={handleCloseModal}
        record={modalDetailsRecord}
        loading={getEntityDetails ? detailsLoading : false}
        error={getEntityDetails ? detailsError : null}
        title={t('Record Details')}
      />
    </div>
  );
};

export default ListItemsDataTable;